import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UpdateProviderData {
  business_name?: string;
  phone?: string;
  email?: string;
  whatsapp?: string;
  description?: string;
  services?: string[];
  languages?: string[];
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  schedule?: any;
  photos?: string[];
}

export const useProviderUpdate = (userId?: string) => {
  const queryClient = useQueryClient();

  const updateProfile = useMutation({
    mutationFn: async (data: UpdateProviderData) => {
      console.log('🔵 [Provider Update] Updating profile:', data);
      
      const { data: result, error } = await supabase.functions.invoke(
        'update-provider-profile',
        { body: data }
      );

      if (error) {
        console.error('❌ [Provider Update] Error:', error);
        throw error;
      }
      
      console.log('✅ [Provider Update] Success:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-profile', userId] });
      toast.success('Profile updated successfully');
    },
    onError: (error: any) => {
      console.error('❌ [Provider Update] Mutation error:', error);
      toast.error(error.message || 'Failed to update profile');
    },
  });

  const deletePhoto = useMutation({
    mutationFn: async (photoUrl: string) => {
      // Extract path from URL
      const url = new URL(photoUrl);
      const path = url.pathname.split('/provider-photos/')[1];

      const { error } = await supabase.storage
        .from('provider-photos')
        .remove([path]);

      if (error) throw error;

      // Update provider photos array
      const { data: provider } = await supabase
        .from('service_providers')
        .select('photos')
        .eq('user_id', userId)
        .single();

      if (provider && Array.isArray(provider.photos)) {
        const updatedPhotos = (provider.photos as string[]).filter((p: string) => p !== photoUrl);
        await supabase
          .from('service_providers')
          .update({ photos: updatedPhotos })
          .eq('user_id', userId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-profile', userId] });
      toast.success('Photo deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete photo');
    },
  });

  return {
    updateProfile: updateProfile.mutateAsync,
    deletePhoto: deletePhoto.mutate,
    isUpdating: updateProfile.isPending,
    isDeleting: deletePhoto.isPending,
  };
};
